import React, { useState, useRef, useEffect, cloneElement } from 'react';
import { cn } from '../../utils/cn';
import { Portal } from '../../utils/Portal';
import { useFloatingPosition } from '../../utils/useFloatingPosition';

export function Popover({ trigger, side = 'bottom', align = 'start', offset = 8, onOpenChange, className, children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const floatingRef = useRef(null);

  const { style } = useFloatingPosition({ triggerRef, floatingRef, open, side, align, offset });

  const setOpenAndNotify = (next) => {
    setOpen((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved !== prev) onOpenChange?.(resolved);
      return resolved;
    });
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (floatingRef.current?.contains(e.target)) return;
      setOpenAndNotify(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenAndNotify(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const triggerEl = cloneElement(trigger, {
    onClick: (e) => { setOpenAndNotify((o) => !o); trigger.props.onClick?.(e); },
    'aria-expanded': open,
    'aria-haspopup': 'dialog',
  });

  return (
    <>
      <span ref={triggerRef} className="inline-block">
        {triggerEl}
      </span>
      {open && (
        <Portal>
          <div
            ref={floatingRef}
            role="dialog"
            data-ds-theme="default"
            style={style}
            className={cn(
              'z-overlay min-w-[220px] p-2 bg-surface border border-border rounded-lg shadow-md',
              'animate-scale-in origin-top-left',
              className,
            )}
          >
            {typeof children === 'function' ? children({ close: () => setOpenAndNotify(false) }) : children}
          </div>
        </Portal>
      )}
    </>
  );
}

export default Popover;
